/*
  Featured products carousel: infinite loop, arrow steps and mouse drag.

  Loop: Concept's <slider-element> stops at both ends. To make it wrap, the
  row of real slides gets one copy before it and one after it, and the
  scroller rests on the real row. Arrows and swipes move normally, into a copy
  if need be; once scrolling settles on a copy, the scroller jumps by exactly
  one row width to the identical real card. Copies are pixel-identical and the
  jump lands on the same snap point, so it cannot be seen. The slider's own
  paging, snapping and editor behaviour are untouched: this only switches on
  its built-in "looping" flag (which stops it disabling the arrows) for this
  one instance. Copies are aria-hidden and out of the tab order, but stay
  clickable. When every card already fits there is nothing to loop, and the
  row is left exactly as it was.

  Arrow steps: each arrow click moves data-arrow-step products (the section's
  "Products per arrow click", 3 by default), never more than are fully in
  view, so no product is skipped unseen.
*/
// Guarded because the script tag repeats when a page has two carousels.
if (!window.FeaturedCarouselController) {
  class FeaturedCarouselController {
    constructor(slider) {
      this.slider = slider;
      this.originals = Array.from(slider.querySelectorAll(':scope > .featured-carousel__slide'));
      this.loop = slider.hasAttribute('data-loop');
      this.step = Math.max(1, parseInt(slider.dataset.arrowStep, 10) || 1);
      this.active = false;
      this.index = 0;
      this.onSettledListener = this.onSettled.bind(this);
      this.onScrollListener = this.onScroll.bind(this);

      if (this.originals.length < 2) return;

      // Concept counts cards per page as width / (card + gap), but a row of
      // four has only three gaps, so it comes out one short and the arrows
      // step three cards while four are visible. Adding one gap to the width
      // counts the row correctly. This instance only; other sliders keep theirs.
      Object.defineProperty(slider, 'perPage', { configurable: true, get: () => this.fitCount() });

      slider.addEventListener('scroll', this.onScrollListener, { passive: true });
      slider.addEventListener('scrollend', this.onSettledListener);
      this.setupMouseDrag();

      // Arrow clicks move this.step products. Concept's slider pages by a whole
      // screen of cards (four on desktop); these listeners run first (capture,
      // on the slider itself) and stop its own handler, for this carousel only.
      slider.addEventListener('slider:next', (event) => this.onArrow(event, 1), true);
      slider.addEventListener('slider:previous', (event) => this.onArrow(event, -1), true);

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(slider);
    }

    // offsetLeft is whole pixels, so card-to-card distance can be off by a
    // fraction; multiplied across a row that drifts a few pixels. Once the
    // copies are in, the row is measured directly instead.
    get metrics() {
      const count = this.originals.length;
      if (this.active) {
        const row = this.originals[0].offsetLeft - this.slider.firstElementChild.offsetLeft;
        return { offset: row / count, row };
      }
      const offset = this.originals[1].offsetLeft - this.originals[0].offsetLeft;
      return { offset, row: offset * count };
    }

    fitCount() {
      const style = getComputedStyle(this.slider);
      const gap = parseFloat(style.columnGap) || 0;
      const width = this.slider.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const { offset } = this.metrics;
      return offset ? Math.max(1, Math.floor((width + gap + 1) / offset)) : 1;
    }

    get perPage() {
      return this.fitCount();
    }

    overflows() {
      const style = getComputedStyle(this.slider);
      const gap = parseFloat(style.columnGap) || 0;
      const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      return this.metrics.row - gap + padding > this.slider.clientWidth + 1;
    }

    refresh() {
      if (!this.slider.isConnected) return this.resizeObserver.disconnect();

      const overflows = this.overflows();
      if (this.loop) {
        if (overflows && !this.active) this.enable();
        else if (!overflows && this.active) this.disable();
        else if (this.active) this.jumpTo(this.index);
      }
    }

    clone(slide) {
      const copy = slide.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('data-loop-clone', '');
      copy.removeAttribute('data-shopify-editor-block');
      copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      copy.querySelectorAll('a, button, input, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
      return copy;
    }

    enable() {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      this.originals.forEach((slide) => {
        before.append(this.clone(slide));
        after.append(this.clone(slide));
      });
      this.slider.prepend(before);
      this.slider.append(after);

      const slider = this.slider;
      Object.defineProperty(slider, 'looping', { configurable: true, get: () => true });
      slider.updateButtons = () => {
        ['slider:previousStatus', 'slider:nextStatus'].forEach((type) => {
          slider.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { status: 'visible' } }));
        });
      };
      slider.reset?.();
      slider.updateButtons();

      this.active = true;
      this.jumpTo(this.index);
    }

    disable() {
      this.slider.querySelectorAll(':scope > [data-loop-clone]').forEach((copy) => copy.remove());

      delete this.slider.looping;
      delete this.slider.updateButtons;
      this.active = false;

      this.slider.reset?.();
      this.scrollInstantly(this.index * this.metrics.offset);
      this.slider.updateButtons?.();
    }

    jumpTo(index) {
      const { offset, row } = this.metrics;
      this.scrollInstantly(row + index * offset);
    }

    scrollInstantly(left) {
      this.slider.scrollTo({ left, behavior: 'instant' });
    }

    currentIndex() {
      const { offset, row } = this.metrics;
      const count = this.originals.length;
      if (this.active) {
        return ((Math.round((this.slider.scrollLeft - row) / offset) % count) + count) % count;
      }
      return Math.min(count - 1, Math.round(this.slider.scrollLeft / offset));
    }

    onScroll() {
      // Browsers without the scrollend event (older Safari) get a short debounce.
      if (!('onscrollend' in window)) {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(this.onSettledListener, 150);
      }
    }

    // Steps from wherever the last click was heading, so quick repeated clicks
    // each count instead of landing on the same card. Never further than the
    // cards fully in view, so no product goes past unseen.
    onArrow(event, direction) {
      event.stopImmediatePropagation();

      const { offset } = this.metrics;
      const step = Math.min(this.step, this.perPage);
      const max = this.slider.scrollWidth - this.slider.clientWidth;
      const base = this.pendingLeft ?? Math.round(this.slider.scrollLeft / offset) * offset;
      const left = Math.max(0, Math.min(base + direction * step * offset, max));

      this.pendingLeft = left;
      this.slider.scrollTo({ left, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    onSettled() {
      this.pendingLeft = null;
      // Mid-drag the row follows the cursor; wrapping here would jump it.
      if (this.drag) return;
      if (this.releasing) this.finishRelease();
      if (this.active) this.normalize();
    }

    /*
      Mouse drag. Scroll containers take touch swipes and trackpads natively,
      but not click-and-drag, so that part is added here, for mouse only
      (touch keeps the browser's own momentum scrolling). While dragging, the
      row follows the cursor 1:1 with snapping paused; on release it eases to
      the nearest card, or on to the next one after a quick flick. A drag is
      never read as a click on the card underneath, and when looping the row
      re-centres mid-drag so it can be dragged endlessly in either direction.
    */
    setupMouseDrag() {
      this.onPointerDownListener = this.onPointerDown.bind(this);
      this.onPointerMoveListener = this.onPointerMove.bind(this);
      this.onPointerUpListener = this.onPointerUp.bind(this);

      this.slider.addEventListener('pointerdown', this.onPointerDownListener);
      this.slider.addEventListener('dragstart', (event) => event.preventDefault());
      this.slider.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          this.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.slider.scrollWidth <= this.slider.clientWidth + 1) return;

      this.drag = {
        startX: event.clientX,
        startLeft: this.slider.scrollLeft,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        velocity: 0,
        moved: false,
      };
      window.addEventListener('pointermove', this.onPointerMoveListener);
      window.addEventListener('pointerup', this.onPointerUpListener);
      window.addEventListener('pointercancel', this.onPointerUpListener);
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag) return;

      const distance = event.clientX - drag.startX;
      if (!drag.moved) {
        // A few pixels of slack, so a slightly shaky click is still a click.
        if (Math.abs(distance) < 6) return;
        drag.moved = true;
        this.releasing = false;
        clearTimeout(this.releaseTimer);
        this.slider.classList.add('is-dragging');
      }
      event.preventDefault();

      let left = drag.startLeft - distance;
      if (this.active) {
        const { row } = this.metrics;
        if (left < row / 2) {
          left += row;
          drag.startLeft += row;
        } else if (left > row * 1.5) {
          left -= row;
          drag.startLeft -= row;
        }
      }
      this.slider.scrollLeft = left;

      const elapsed = event.timeStamp - drag.lastTime;
      if (elapsed > 0) {
        drag.velocity = drag.velocity * 0.4 + ((event.clientX - drag.lastX) / elapsed) * 0.6;
        drag.lastX = event.clientX;
        drag.lastTime = event.timeStamp;
      }
    }

    onPointerUp(event) {
      window.removeEventListener('pointermove', this.onPointerMoveListener);
      window.removeEventListener('pointerup', this.onPointerUpListener);
      window.removeEventListener('pointercancel', this.onPointerUpListener);

      const drag = this.drag;
      this.drag = null;
      if (!drag || !drag.moved) return;

      // The click that follows this pointerup belongs to the drag.
      this.suppressClick = event.type === 'pointerup';
      setTimeout(() => (this.suppressClick = false), 0);

      const { offset } = this.metrics;
      const position = this.slider.scrollLeft / offset;
      // A drag that ended still moving (a flick) carries on to the next card
      // in that direction; a slow release settles on the nearest.
      const stale = event.timeStamp - drag.lastTime > 80;
      const velocity = stale ? 0 : drag.velocity;
      let index = Math.round(position);
      if (velocity < -0.3) index = Math.ceil(position);
      else if (velocity > 0.3) index = Math.floor(position);

      const max = this.slider.scrollWidth - this.slider.clientWidth;
      const target = Math.max(0, Math.min(index * offset, max));

      this.releasing = true;
      this.slider.scrollTo({ left: target, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      // scrollend normally finishes the release; this covers a release that
      // needed no movement, where no scrollend fires.
      this.releaseTimer = setTimeout(() => this.onSettled(), 700);
    }

    finishRelease() {
      this.releasing = false;
      clearTimeout(this.releaseTimer);
      this.slider.classList.remove('is-dragging');
    }

    normalize() {
      const { offset, row } = this.metrics;
      let left = this.slider.scrollLeft;

      // Half a card of slack either side, so rounding never triggers a wrap.
      if (left < row - offset / 2) left += row;
      else if (left > row * 2 - offset / 2) left -= row;

      if (left !== this.slider.scrollLeft) this.scrollInstantly(left);
      this.index = this.currentIndex();
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.whenDefined('slider-element').then(() => {
    const init = (root) => {
      root.querySelectorAll('slider-element.featured-carousel__track').forEach((slider) => {
        if (!slider.featuredCarousel) slider.featuredCarousel = new FeaturedCarouselController(slider);
      });
    };

    init(document);
    document.addEventListener('shopify:section:load', (event) => init(event.target));
  });

  // NB11 test: card clicks to GA through GTM. One listener for every carousel
  // on the page. Only the card links carry data-nb-fp-product, so arrows never
  // count, and a drag is never a click (the slider stops it before it gets
  // here). Nothing is prevented or awaited, so the product page opens as usual.
  // window.nbTrack (layout/theme.liquid) sends it to GTM.
  // The arrival is reported by the product page itself: the card link
  // carries nb_fp=<position> (featured-products-carousel-card.liquid).
  const cardFields = (link) => ({
    nb_product: link.dataset.nbFpProduct,
    nb_variant: link.dataset.nbFpVariant,
    nb_variant_title: link.dataset.nbFpVariantTitle || '',
    nb_position: Number(link.dataset.nbFpPosition),
    nb_arm: window.nbArm || '',
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-nb-fp-product]');
    if (!link) return;
    window.nbTrack({ event: 'nb_fp_card_click', ...cardFields(link) });
  });

  // NB11 test: a card counts as seen (nb_fp_card_view) once per page, the first
  // time half of it is on screen. Only real cards are watched: the loop's copies
  // are added later and the scroller always settles back on the real card.
  // The ratio is checked because the observer's first report fires for every
  // card, with isIntersecting true for even a sliver.
  const seen = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio < 0.5) return;
      seen.unobserve(entry.target);
      window.nbTrack({ event: 'nb_fp_card_view', ...cardFields(entry.target) });
    });
  }, { threshold: 0.5 });
  document.querySelectorAll('a[data-nb-fp-product]').forEach((link) => {
    if (!link.closest('[data-loop-clone]')) seen.observe(link);
  });

  window.FeaturedCarouselController = FeaturedCarouselController;
}
